import React, { useState, useEffect, useRef } from "react";
import { PageHeader, ListGroup, ListGroupItem, FormGroup, FormControl, Button, Glyphicon } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import "./Home.css";
import { get } from 'aws-amplify/api';
import utils from "../utils";
import config from "../config";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";

export default function Home(props) {
  const [loginDetails, setLoginDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [sortBy, setSortBy] = useState("name"); // name, date
  const [viewMode, setViewMode] = useState("list"); // list, grid
  const searchInputRef = useRef(null);

  // Keyboard shortcuts
  useKeyboardShortcuts([
    {
      key: 'k',
      ctrl: true,
      action: () => {
        // Focus search bar (Ctrl/Cmd + K)
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    },
    {
      key: 'i',
      ctrl: true,
      action: () => {
        // New login detail (Ctrl/Cmd + I)
        props.history.push('/logindetails/new');
      }
    }
  ], props.isAuthenticated);

  useEffect(() => {
    async function onLoad() {
      if (!props.isAuthenticated) {
        return;
      }
  
      try {
        const loginDetails = await loadLoginDetails();
        setLoginDetails(loginDetails);
      } catch (e) {
        console.error('Error loading login details:', e);
        alert(`Error loading data: ${e.message || e}`);
      }
  
      setIsLoading(false);
    }
  
    onLoad();
  }, [props.isAuthenticated]);
  
  async function loadLoginDetails() {
    try {
      console.log('Making API call to:', config.apiGateway.URL + '/logindetails');
      const restOperation = get({
        apiName: "logindetails",
        path: "/logindetails"
      });
      const response = await restOperation.response;
      console.log('API response status:', response.statusCode);
      const data = await response.body.json();
      return data;
    } catch (error) {
      console.error('API call error:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      if (error.response) {
        console.error('Response status:', error.response.statusCode);
        console.error('Response body:', error.response.body);
      }
      throw error;
    }
  }

  function copyToClipboard(text, id, type) {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(`${id}-${type}`);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  function handleCopyUsername(e, loginDetail) {
    e.preventDefault();
    e.stopPropagation();
    const username = utils.decrypt(loginDetail.loginId);
    copyToClipboard(username, loginDetail.loginDetailId, 'username');
  }

  function handleCopyPassword(e, loginDetail) {
    e.preventDefault();
    e.stopPropagation();
    const password = utils.decrypt(loginDetail.password);
    copyToClipboard(password, loginDetail.loginDetailId, 'password');
  }

  function handleImportSourceClick(e, importSource) {
    e.preventDefault();
    e.stopPropagation();
    setSearchQuery(importSource);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }

  function renderLoginDetailsGrid(loginDetails) {
    return (
      <div className="grid-container">
        <div 
          className="grid-item add-new" 
          onClick={() => props.history.push('/logindetails/new')}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '150px',
            fontSize: '18px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          + Add a new login detail
        </div>
        {loginDetails.map((loginDetail) => (
          <div 
            key={loginDetail.loginDetailId} 
            className="grid-item"
            onClick={() => props.history.push(`/logindetails/${loginDetail.loginDetailId}`)}
          >
            <div className="grid-item-content">
              <h4>{utils.decrypt(loginDetail.websiteName).trim().split("\n")[0]}</h4>
              <p className="grid-username">{utils.decrypt(loginDetail.loginId)}</p>
              {loginDetail.importSource && (
                <span 
                  className="import-badge" 
                  title={`Imported from: ${loginDetail.importSource}\nDate: ${new Date(loginDetail.importDate).toLocaleDateString()}\nClick to filter`}
                  onClick={(e) => handleImportSourceClick(e, loginDetail.importSource)}
                >
                  📥 {loginDetail.importSource}
                </span>
              )}
            </div>
            <div className="grid-item-actions">
              <Button 
                bsSize="xsmall" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyUsername(e, loginDetail);
                }}
                title="Copy Username"
              >
                <Glyphicon glyph="user" /> {copiedId === `${loginDetail.loginDetailId}-username` ? '✓' : ''}
              </Button>
              <Button 
                bsSize="xsmall" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyPassword(e, loginDetail);
                }}
                title="Copy Password"
              >
                <Glyphicon glyph="lock" /> {copiedId === `${loginDetail.loginDetailId}-password` ? '✓' : ''}
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }

  function renderLoginDetailsList(loginDetails) {
    return [{}].concat(loginDetails).map((loginDetail, i) =>
      i !== 0 ? (
        <ListGroupItem 
          key={loginDetail.loginDetailId} 
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <LinkContainer to={`/logindetails/${loginDetail.loginDetailId}`} style={{ flex: 1, cursor: 'pointer' }}>
                <span>{utils.decrypt(loginDetail.websiteName).trim().split("\n")[0]}</span>
              </LinkContainer>
              <div style={{ display: 'flex', gap: '5px' }}>
                <Button 
                  bsSize="xsmall" 
                  onClick={(e) => handleCopyUsername(e, loginDetail)}
                  title="Copy Username"
                >
                  <Glyphicon glyph="user" /> {copiedId === `${loginDetail.loginDetailId}-username` ? '✓' : ''}
                </Button>
                <Button 
                  bsSize="xsmall" 
                  onClick={(e) => handleCopyPassword(e, loginDetail)}
                  title="Copy Password"
                >
                  <Glyphicon glyph="lock" /> {copiedId === `${loginDetail.loginDetailId}-password` ? '✓' : ''}
                </Button>
              </div>
            </div>
          }
        >
          <LinkContainer to={`/logindetails/${loginDetail.loginDetailId}`} style={{ cursor: 'pointer' }}>
            <div>
              <span>{utils.decrypt(loginDetail.loginId)}</span>
              {loginDetail.importSource && (
                <span 
                  className="import-badge" 
                  title={`Imported from: ${loginDetail.importSource}\nDate: ${new Date(loginDetail.importDate).toLocaleDateString()}\nClick to filter`}
                  onClick={(e) => handleImportSourceClick(e, loginDetail.importSource)}
                >
                  📥 {loginDetail.importSource}
                </span>
              )}
            </div>
          </LinkContainer>
        </ListGroupItem>
      ) : (
        <LinkContainer key="new" to="/logindetails/new">
          <ListGroupItem>
            <h4>
              <b>{"\uFF0B"}</b> Add a new login detail
            </h4>
          </ListGroupItem>
        </LinkContainer>
      )
    );
  }

  function renderLander() {
    return (
      <div className="lander">
        <h1>Scratch</h1>
        <p>A simple password manager app</p>
      </div>
    );
  }

  function renderLoginDetails() {
    // Filter by search query (search across website name, login ID, URL, and import source)
    const filtered = loginDetails.filter(ld => {
      const websiteName = utils.decrypt(ld.websiteName).toLowerCase();
      const loginId = utils.decrypt(ld.loginId).toLowerCase();
      const websiteUrl = utils.decrypt(ld.websiteUrl || "").toLowerCase();
      const importSource = (ld.importSource || "").toLowerCase();
      const query = searchQuery.toLowerCase();
      
      return websiteName.includes(query) || 
             loginId.includes(query) || 
             websiteUrl.includes(query) ||
             importSource.includes(query);
    });

    // Sort the filtered results
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "name") {
        const nameA = utils.decrypt(a.websiteName).toLowerCase();
        const nameB = utils.decrypt(b.websiteName).toLowerCase();
        return nameA.localeCompare(nameB);
      } else if (sortBy === "date") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return 0;
    });

    return (
      <div className="notes">
        <PageHeader>Your login details</PageHeader>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
          <FormGroup style={{ flex: 1, marginBottom: 0 }}>
            <FormControl
              inputRef={ref => searchInputRef.current = ref}
              type="text"
              placeholder="Search by website name, username, or URL... (Ctrl/Cmd + K)"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </FormGroup>
          <FormGroup style={{ width: '150px', marginBottom: 0 }}>
            <FormControl
              componentClass="select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="name">Sort by Name</option>
              <option value="date">Sort by Date</option>
            </FormControl>
          </FormGroup>
          <div style={{ display: 'flex', gap: '5px' }}>
            <Button 
              onClick={() => setViewMode('list')}
              bsStyle={viewMode === 'list' ? 'primary' : 'default'}
              title="List View"
            >
              <Glyphicon glyph="th-list" />
            </Button>
            <Button 
              onClick={() => setViewMode('grid')}
              bsStyle={viewMode === 'grid' ? 'primary' : 'default'}
              title="Grid View"
            >
              <Glyphicon glyph="th" />
            </Button>
          </div>
        </div>
        {searchQuery && (
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>
            Found {sorted.length} result{sorted.length !== 1 ? 's' : ''}
          </p>
        )}
        {viewMode === 'list' ? (
          <ListGroup>
            {!isLoading && renderLoginDetailsList(sorted)}
          </ListGroup>
        ) : (
          !isLoading && renderLoginDetailsGrid(sorted)
        )}
      </div>
    );
  }

  return (
    <div className="Home">
      {props.isAuthenticated ? renderLoginDetails(loginDetails) : renderLander()}
    </div>
  );
}